import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  IconPicker,
  Input,
  ScrollArea,
  Spinner,
  Text,
  useIsMobile,
  type Color,
  type IconName,
} from "@salestrak/ui";

import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";
import { EditableField } from "#app/components/settings/editable-field";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function TeamEditPage() {
  const isMobile = useIsMobile();
  const { teamId } = Route.useParams();
  const { push, replace } = useAppNavigate();
  const { updateTeam, deleteTeam } = useTeamMutations();
  const { team, glyph, loading, error } = useTeamDetail(teamId);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handleFieldSave = useCallback(
    async (fieldId: string, value: string) => {
      if (!teamId) return;
      await updateTeam(teamId, { [fieldId]: value });
    },
    [teamId, updateTeam],
  );

  const handleIconChange = useCallback(
    async (icon: IconName) => {
      if (!teamId || !team) return;
      await updateTeam(teamId, { icon: { icon, color: glyph.color } });
    },
    [teamId, team, updateTeam, glyph.color],
  );

  const handleColorChange = useCallback(
    async (color: Color) => {
      if (!teamId || !team) return;
      await updateTeam(teamId, { icon: { icon: glyph.icon, color } });
    },
    [teamId, team, updateTeam, glyph.icon],
  );

  const handleDelete = useCallback(async () => {
    if (!teamId) return;
    await deleteTeam(teamId);
    setDeleteOpen(false);
    replace("/settings/teams");
  }, [teamId, deleteTeam, replace]);

  if (loading && !team) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!team) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load team</EmptyTitle>
            {error ? (
              <EmptyDescription>{error.message}</EmptyDescription>
            ) : null}
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  const canDeleteConfirm =
    deleteConfirmText.toLowerCase() === team.teamName.toLowerCase();

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Teams" onClick={() => replace("/settings/teams")} />
          <PageHeaderTitle
            adornment={
              <Text size="sm" variant="muted">{team?.members?.length ?? 0} members</Text>
            }
          >
            {team.teamName}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <SettingsGroup label="General">
              <SettingsGroupRow label="Name & Icon">
                <Box direction="row" gap="sm" align="center" fill={isMobile ? "width" : undefined}>
                  <IconPicker
                    icon={glyph.icon}
                    color={glyph.color}
                    onIconChange={handleIconChange}
                    onColorChange={handleColorChange}
                    size="sm"
                  />
                  <Box fill={isMobile ? "flex" : undefined}>
                    <EditableField
                      fieldId="teamName"
                      label="Team name"
                      initialValue={team.teamName}
                      onSave={handleFieldSave}
                      placeholder="Team name"
                      width={isMobile ? "full" : "xl"}
                    />
                  </Box>
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Identifier"
                subtitle="Unique key for this team"
              >
                <Box width={isMobile ? undefined : 232} fill={isMobile ? "width" : undefined}>
                  <EditableField
                    fieldId="teamKey"
                    label="Team key"
                    initialValue={team.teamKey ?? ""}
                    onSave={handleFieldSave}
                    placeholder="TEAM-KEY"
                    width="full"
                  />
                </Box>
              </SettingsGroupRow>
            </SettingsGroup>

            <SettingsGroup>
              <SettingsGroupRow
                label="Teammates"
                subtitle="Manage teammates and roles"
                leading={<Icon name="users" size="sm" color="muted" />}
                onClick={() => push(`/settings/teams/${teamId}/members`)}
                actionsVisible
              >
                <Box direction="row" gap="sm" align="center">
                  <Text size="sm" variant="muted">
                    {team?.members?.length ?? 0} teammates
                  </Text>
                  <Icon name="chevron-right" color="muted" size="sm" />
                </Box>
              </SettingsGroupRow>
            </SettingsGroup>

            <SettingsGroup label="Danger zone">
              <SettingsGroupRow
                label="Delete this team"
                subtitle="Permanently remove this team. Projects will be unassigned."
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive-foreground"
                  onClick={() => {
                    setDeleteConfirmText("");
                    setDeleteOpen(true);
                  }}
                >
                  <Text>Delete team...</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete the {team.teamName} team?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This team will be permanently deleted, along with the following
              results:{"\n\n"}To confirm, type {team.teamName} below:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Box paddingX="lg" paddingBottom="sm">
            <Input
              value={deleteConfirmText}
              onChange={(event) => setDeleteConfirmText(event.target.value)}
              placeholder={team.teamName}
            />
          </Box>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canDeleteConfirm}
            >
              <Text>Delete team</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/teams/$teamId/",
)({ component: withPermission("manage.teams", TeamEditPage) });
